import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiTrash2, FiSearch, FiChevronDown, FiChevronUp } from "react-icons/fi";

import {
  fetchContacts,
  deleteMessage,
} from "../../redux/actions/WebAdminActions";

const ContactDetails = () => {
  const dispatch = useDispatch();

  const { contacts, loading, error } = useSelector((state) => state.webAdmin);

  const [expandedMessageIds, setExpandedMessageIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // delete modal state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    dispatch(fetchContacts());
  }, []);

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = () => {
    dispatch(deleteMessage("/v1/api/admin/deleteContactMessage", deleteId));
    setConfirmDeleteOpen(false);
    setDeleteId(null);
  };

  const toggleExpand = (id) => {
    setExpandedMessageIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const filteredContacts = contacts.filter(
    (c) =>
      c.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.subject.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
            Contact Messages
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View and manage messages submitted from the portfolio website
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
          <input
            type="text"
            placeholder="Search by name, email, company, or subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
          />
        </div>
      </div>

      {loading && (
        <p className="text-gray-600 dark:text-gray-300">Loading messages...</p>
      )}
      {error && <p className="text-red-500 mb-4">{error}</p>}

      <div className="overflow-x-auto shadow-lg rounded-2xl border border-gray-200 dark:border-gray-700">
        <table className="min-w-full bg-white dark:bg-gray-800 border-collapse">
          <thead className="bg-gray-100 dark:bg-gray-700 shadow-[0_2px_10px_rgba(0,0,0,0.05)] relative z-10">
            <tr>
              <th className="py-4 px-4 text-left font-semibold text-gray-800 dark:text-white border-b">
                #
              </th>
              <th className="py-4 px-4 text-left font-semibold text-gray-800 dark:text-white border-b">
                Sender Details
              </th>
              <th className="py-4 px-4 text-left font-semibold text-gray-800 dark:text-white border-b">
                Company & Subject
              </th>
              <th className="py-4 px-4 text-left font-semibold text-gray-800 dark:text-white border-b">
                Message
              </th>
              <th className="py-4 px-4 text-left font-semibold text-gray-800 dark:text-white border-b">
                Received At
              </th>
              <th className="py-4 px-4 text-center font-semibold text-gray-800 dark:text-white border-b">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {filteredContacts.map((contact, index) => {
              const isExpanded = expandedMessageIds.includes(contact.id);
              const isLongMessage = contact.message.length > 50;

              return (
                <tr
                  key={contact.id}
                  className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="py-4 px-4 text-gray-500 dark:text-gray-400 text-sm">
                    {index + 1}
                  </td>

                  <td className="py-4 px-4">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {contact.full_name}
                    </div>
                    <div className="text-sm text-orange-600 dark:text-orange-500/70 italic">
                      {contact.email}
                    </div>
                  </td>

                  <td className="py-4 px-4">
                    <div className="text-sm font-bold text-blue-800 dark:text-blue-300 font-sans tracking-wide uppercase">
                      {contact.company}
                    </div>

                    <div className="text-xs text-gray-500 dark:text-gray-400 font-serif italic mt-0.5">
                      {contact.subject}
                    </div>
                  </td>

                  <td className="py-4 px-4 max-w-xs">
                    <div className="text-sm italic font-serif text-gray-700 dark:text-gray-300 flex items-start gap-1">
                      <span>
                        {isLongMessage && !isExpanded
                          ? `"${contact.message.slice(0, 50)}..."`
                          : `"${contact.message}"`}
                      </span>
                      {isLongMessage && (
                        <button
                          onClick={() => toggleExpand(contact.id)}
                          className="mt-1 text-blue-600 dark:text-blue-400 hover:text-blue-800"
                        >
                          {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                        </button>
                      )}
                    </div>
                  </td>

                  <td className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {new Date(contact.created_at).toLocaleString([], {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </td>

                  <td className="py-4 px-4 text-center">
                    <button
                      onClick={() => handleDelete(contact.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-500 text-red-600 hover:bg-red-600 hover:text-white rounded-lg"
                    >
                      <FiTrash2 size={14} />
                      <span className="text-xs font-bold uppercase">
                        Delete
                      </span>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredContacts.length === 0 && !loading && (
          <div className="bg-white dark:bg-gray-800 p-8 text-center text-gray-500">
            No messages found.
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {confirmDeleteOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full">
                <FiTrash2 className="text-red-600 dark:text-red-400 text-xl" />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                Delete Contact Message
              </h3>

              <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                Are you sure you want to delete this message? This action cannot
                be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDeleteOpen(false)}
                  className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>

                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactDetails;
